// Pannello avvisi critici e SLA breach
import { Link } from 'react-router-dom'
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react'
import { Card, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Badge } from '../../../components/ui/Badge'
import { formatRelativeDate } from '../../../lib/utils'

interface DashboardAlert {
  id: string
  type: 'sla_breach' | 'escalated' | 'unassigned'
  ticketId: string
  ticketNumber: string
  title: string
  severity: 'high' | 'critical'
  createdAt: string
}

interface AlertsPanelProps {
  alerts?: DashboardAlert[]
}

const TYPE_LABELS: Record<DashboardAlert['type'], string> = {
  sla_breach: 'SLA scaduto',
  escalated: 'Escalato',
  unassigned: 'Non assegnato',
}

export function AlertsPanel({ alerts = [] }: AlertsPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            Avvisi attivi
            {alerts.length > 0 && (
              <Badge variant="danger">{alerts.length}</Badge>
            )}
          </span>
        </CardTitle>
      </CardHeader>

      {alerts.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--fc-success)_15%,transparent)]">
            <AlertTriangle className="h-5 w-5 text-[var(--fc-success)]" aria-hidden="true" />
          </div>
          <p className="text-sm text-[var(--fc-foreground-muted)]">Nessun avviso attivo</p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--fc-border)]" role="list">
          {alerts.map((alert) => (
            <li key={alert.id} className="py-3">
              <Link
                to={`/tickets/${alert.ticketId}`}
                className="flex items-start gap-3 hover:bg-[var(--fc-surface-raised)] -mx-4 px-4 rounded transition-colors"
              >
                <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  alert.severity === 'critical'
                    ? 'bg-[color-mix(in_srgb,var(--fc-danger)_15%,transparent)]'
                    : 'bg-[color-mix(in_srgb,var(--fc-warning)_15%,transparent)]'
                }`}>
                  {alert.type === 'sla_breach' ? (
                    <Clock className={`h-3 w-3 ${alert.severity === 'critical' ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-warning)]'}`} />
                  ) : (
                    <AlertTriangle className={`h-3 w-3 ${alert.severity === 'critical' ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-warning)]'}`} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">
                      #{alert.ticketNumber}
                    </span>
                    <Badge variant={alert.severity === 'critical' ? 'danger' : 'warning'} dot>
                      {TYPE_LABELS[alert.type]}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-sm text-[var(--fc-foreground)]">{alert.title}</p>
                  <p className="text-xs text-[var(--fc-foreground-subtle)]">
                    {formatRelativeDate(alert.createdAt)}
                  </p>
                </div>

                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-[var(--fc-foreground-subtle)]" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
